<template>
	<div class="container operational-advertise-page">
		<van-nav-bar :title="pageTitle" left-arrow @click-left="goBack" fixed placeholder />

		<van-tabs v-model:active="activeId" class="tab-list" :ellipsis="false" @click-tab="onClickTab"
			:before-change="beforeTabChange">
			<van-tab v-for="(item, idx) in tabList" :key="item.specialAreaId" :title="item.specialAreaName"
				:name="item.specialAreaId" class="tab-item" />
			<div class="view-all">
				<TabListPopover :active-id="activeId" :tab-list="tabList" @click-tab="handlePopoverTab" />
			</div>
		</van-tabs>

		<van-pull-refresh v-model="refreshing" @refresh="onRefresh">
			<van-list v-model:loading="loading" :finished="finished" :error="listError" :error-text="loadErrMsg"
				finished-text="没有更多了" @load="getProductList">
				<div v-for="item in tabList" :key="item.specialAreaId"
					:class="['product-list-section', !item.productList?.length ? 'no-list' : '']"
					:id="`product-list-section_${item.specialAreaId}`">
					<BigImg :data-list="item.productList || []" fromPage="运营广告" />
				</div>
			</van-list>
		</van-pull-refresh>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { querySpecialAreaList, queryProductBySpecialArea } from '@/api/index.js';
import BigImg from '@/components/BigImg.vue';
import TabListPopover from './components/TabListPopover.vue';
import { initObserver } from './scrollObserver';

definePageMeta({
	layout: 'default',
});

const route = useRoute();
const router = useRouter();

const pageTitle = ref('热销推荐');
const itemId = ref(0);
const tabList = ref([]);
const refreshing = ref(false);
const loading = ref(false);
const finished = ref(false);
const activeId = ref(-1);
const pageStart = ref(0);
const listError = ref(false);
const loadErrMsg = ref('');
const isClickTab = ref(false);
const remover = ref(null);
const disableScroll = ref(false);

const curTabIndex = computed(() => tabList.value.findIndex(item => item.specialAreaId === activeId.value));
const curTab = computed(() => tabList.value[curTabIndex.value]);

useHead({
	title: () => `国械在线-${pageTitle.value}`,
});

onMounted(() => {
	const title = route.query.title;
	const id = route.query.itemId;
	if (title) pageTitle.value = title;
	if (id) itemId.value = Number(id);
	querySpecialAreaListData();

	setTimeout(() => {
		const targetElement = document.querySelectorAll('.operational-advertise-page .product-list-section');
		remover.value = initObserver(targetElement, inOut);
	}, 1000);
});

onUnmounted(() => {
	if (remover.value) remover.value();
});

function goBack() {
	router.back();
}

function beforeTabChange() {
	return true;
}

function setError() {
	listError.value = false;
	loadErrMsg.value = '';
}

function handlePopoverTab(specialAreaId, specialAreaName) {
	onClickTab({ name: specialAreaId });
}

function onClickTab({ name: tabId }) {
	activeId.value = tabId;
	if (curTab.value?.linkUrl) {
		navigateTo(curTab.value.linkUrl, {
			external: true
		});
		return;
	}
	const target = tabList.value.find(item => item.specialAreaId === tabId);
	if (target?.productList?.length || target?.fetched) {
		sectionIntoView(tabId);
	} else {
		tabList.value.forEach(tabItem => {
			if (tabItem.specialAreaId !== tabId) {
				tabItem.productList = [];
				tabItem.fetched = false;
				tabItem.total = 0;
			}
		});
		isClickTab.value = true;
		onRefresh();
	}
}

function sectionIntoView(tabId) {
	disableScroll.value = true;
	const str = 'product-list-section_' + tabId;
	const element = document.getElementById(str);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		setTimeout(() => {
			disableScroll.value = false;
		}, 700);
	}
}

function onRefresh() {
	if (curTabIndex.value !== 0 && !isClickTab.value) {
		activeId.value = tabList.value[curTabIndex.value - 1].specialAreaId;
		const tabId = activeId.value;
		const target = tabList.value.find(item => item.specialAreaId === tabId);
		if (!target?.productList?.length) {
			tabList.value.forEach(tabItem => {
				if (tabItem.specialAreaId !== tabId) {
					tabItem.productList = [];
				}
			});
		}
	} else if (curTabIndex.value === 0) {
		tabList.value.forEach(tabItem => {
			tabItem.productList = [];
		});
	}

	setError();
	pageStart.value = 0;
	finished.value = false;
	loading.value = true;
	refreshing.value = true;
	getProductList();
}

async function getProductList() {
	if (activeId.value === -1) return;
	let reqId = activeId.value;
	const curTabItem = tabList.value[curTabIndex.value];
	const productList = curTabItem?.productList ?? [];
	const total = curTabItem?.total ?? 0;
	const fetched = curTabItem?.fetched ?? false;

	if (productList.length >= total && total > 0) {
		if (fetched) {
			if (curTabIndex.value + 1 < tabList.value.length) {
				reqId = tabList.value[curTabIndex.value + 1].specialAreaId;
			} else {
				loading.value = false;
				finished.value = true;
				return;
			}
		}
		disableScroll.value = true;
		pageStart.value = 0;
	}

	try {
		pageStart.value++;
		const params = {
			specialAreaId: reqId,
			pageStart: pageStart.value,
			pageSize: 10,
		};
		const res = await queryProductBySpecialArea(params);
		const { code, data, message } = res ?? {};
		if (code == 200) {
			if (refreshing.value) {
				refreshing.value = false;
			}
			const currentIdx = reqId === activeId.value ? curTabIndex.value : curTabIndex.value + 1;
			isClickTab.value = false;
			const tab = tabList.value[currentIdx];
			if (tab) {
				tab.productList = [...(tab.productList || []), ...(data?.list || [])];
				tab.total = data?.total ?? 0;
				tab.fetched = true;
			}
			if (data?.list?.length === 0 && data?.total === 0) {
				activeId.value = reqId;
			}
			loading.value = false;
			const lastTab = tabList.value[tabList.value.length - 1];
			if (lastTab && lastTab.productList?.length >= (lastTab.total ?? 0)) {
				finished.value = true;
			}
		} else {
			loading.value = false;
			refreshing.value = false;
			listError.value = true;
			loadErrMsg.value = message || '加载失败';
		}
	} catch (error) {
		listError.value = true;
		loadErrMsg.value = error?.message || '请求失败，点击重新加载';
	} finally {
		loading.value = false;
		disableScroll.value = false;
	}
}

async function querySpecialAreaListData() {
	try {
		const { code, data } = await querySpecialAreaList({ itemId: itemId.value });
		if (code === '200' && data?.length) {
			tabList.value = data.map(item => ({
				...item,
				productList: [],
				total: 0,
				fetched: false,
			}));
			const firstTab = tabList.value[0];
			activeId.value = firstTab.specialAreaId;
			onRefresh();
		}
	} catch (error) {
		console.error('querySpecialAreaList', error);
	}
}

function inOut(type, id) {
	if (disableScroll.value) return;
	if (type === 'in') {
		const notPrefixId = id.replace('product-list-section_', '');
		tabScrollView(notPrefixId);
	}
}

function tabScrollView(id) {
	const index = tabList.value.findIndex(item => item.specialAreaId == id);
	if (index === -1 || loading.value) return;
	activeId.value = +id;
}
</script>

<style lang="scss" scoped>
.container {
	// padding-top: 85px;
}

.view-all {
	display: flex;
	align-items: center;
	color: #666666;
	position: absolute;
	right: 0;
	top: 0;
	height: 100%;
	width: 40px;
	background-color: #fff;
}

.tab-list {
	position: sticky;
	top: 0;
	z-index: 1;

	.tab-item {
		width: fit-content !important;
	}
}

.product-list-section {
	scroll-margin-top: 52px;

	&.no-list {
		margin: 0;
	}
}

:deep(.van-tabs__nav .van-tab--active) {
	color: #455bd9 !important;
}

:deep(.van-tabs__wrap) {
	padding-right: 40px;
}

:deep(.van-tabs__line) {
	background-color: transparent !important;
	background: url('/staticSeo/images/tab-active-line.png') no-repeat center;
	background-size: contain;
	width: 18px !important;
	height: 6.7px !important;
	bottom: 16px !important;
}

:deep(.tab-wrap) {
	font-size: 18px;
	font-weight: 500;
}

:deep(.no-list .mini-list) {
	margin: 0;
}
</style>
